import { useState, useRef, useEffect, forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { FieldLabel, FieldErrorMessage } from "ui/forms";

import { PreviewSidebarInputType } from "containers/admin/clients/client/theme-page/preview/preview-sidebar/preview-sidebar.types";

import Icons from "assets";

const HexInput = forwardRef<HTMLDivElement, PreviewSidebarInputType>(
  (
    {
      inputNameKey,
      value,
      label,
      isRequired,
      isDisabled,
      errMessage,
      handleCustomChange,
      handleBlurForNotRequiredField,
      ...rest
    },
    ref,
  ) => {
    const [isPickerVisible, setIsPickerVisible] = useState(false);
    const colorInputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleFocus = () => {
      if (!isDisabled) {
        setIsPickerVisible(true);
      }
    };

    useEffect(() => {
      if (isDisabled) {
        setIsPickerVisible(false);
      }
    }, [isDisabled]);

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>, field: any) => {
      const currentTarget = event.currentTarget;

      // Delay to allow focus to shift to another child input before checking if we should close
      setTimeout(() => {
        if (!currentTarget.contains(document.activeElement)) {
          setIsPickerVisible(false);

          //in case field is not required
          if (!isRequired && field === "#") {
            setIsPickerVisible(false);

            !!handleBlurForNotRequiredField &&
              handleBlurForNotRequiredField(inputNameKey as string);
          }
        }
      }, 0);
    };

    // logic to handle color picker opening with only one click
    const handleSvgClick = () => {
      setIsPickerVisible(true);

      if (!colorInputRef.current) {
        setTimeout(() => {
          if (colorInputRef.current) {
            colorInputRef.current.click();
            colorInputRef.current.focus();
          }
        }, 0);
      }
    };

    // logic to handle color picker focus on tab
    const handleSvgFocus = () => {
      if (!colorInputRef.current) {
        setTimeout(() => {
          if (colorInputRef.current) {
            colorInputRef.current.focus();
          }
        }, 0);
      }
    };

    //styles
    const colorPickerWrapperStyles = twMerge(
      "flex flex-col gap-1.5 cursor-default",
      !isDisabled && "cursor-pointer",
    );

    const hexInputStyles = twMerge(
      "flex gap-2.5 rounded-md border border-transparent bg-white px-2.5 py-1 text-base font-normal leading-6 h-10 items-center",
      isDisabled && "bg-neutrals-200",
      !isDisabled && !errMessage && "color-picker-input",
      !!errMessage && "border border-critical-700",
    );

    const colorPickerInputStyles = twMerge(
      "bg-transparent rounded-[2px] h-6 w-7 cursor-default color-picker-button",
      !isDisabled && "cursor-pointer",
    );

    const textInputStyles = twMerge(
      "text-base cursor-default focus:outline-none w-full text-neutrals-900 placeholder-gray-500",
      isDisabled && "bg-transparent text-neutrals-500",
    );

    const colorPickerButtonStyles = twMerge(
      "bg-transparent size-6 flex items-center justify-center cursor-default",
      !isDisabled && "cursor-pointer",
    );

    const chooseColorIconStyles = twMerge(
      "w-[25px] h-7 text-neutrals-500",
      !isDisabled && "text-neutrals-200",
    );

    return (
      <div
        role="button"
        className={colorPickerWrapperStyles}
        onFocus={handleFocus}
        onBlur={(e) => handleBlur(e, value)}
        ref={ref || wrapperRef}>
        {!!label && (
          <FieldLabel name={inputNameKey as string} required={isRequired} disabled={isDisabled}>
            {label}
          </FieldLabel>
        )}

        <div className={hexInputStyles} tabIndex={-1}>
          {isPickerVisible || value ? (
            <input
              type="color"
              data-testid={`${inputNameKey}-color-hex-picker`}
              disabled={isDisabled}
              className={colorPickerInputStyles}
              {...rest}
              ref={colorInputRef}
              onInput={(e) => {
                handleCustomChange((e.target as HTMLInputElement).value, true);
              }}
              value={value || ""}
            />
          ) : (
            <button
              type="button"
              data-testid={`${inputNameKey}-svg-icon`}
              className={colorPickerButtonStyles}
              disabled={isDisabled}
              onMouseDown={handleSvgClick}
              onFocus={handleSvgFocus}>
              <Icons.ChooseColor className={chooseColorIconStyles} />
            </button>
          )}

          <input
            type="text"
            data-testid="color-text-input"
            disabled={isDisabled}
            aria-describedby={errMessage ? `${inputNameKey}-error` : undefined}
            className={textInputStyles}
            placeholder="Type hex code..."
            maxLength={7}
            {...rest}
            onFocus={() => {
              if (!value?.startsWith("#")) {
                const newValue = `#${value}`;

                handleCustomChange(newValue);
              }
            }}
            onKeyUp={(e) => {
              const target = e.target as HTMLInputElement;

              const value = !target.value.startsWith("#")
                ? `#${target.value.replace(/#/g, "")}`
                : target.value;

              handleCustomChange(value);
            }}
            value={value || ""}
          />
        </div>

        {!!errMessage && <FieldErrorMessage name={inputNameKey as string} message={errMessage} />}
      </div>
    );
  },
);

export default HexInput;
